// Module Dashboard Super Admin - Pradium Tech
import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiDollarSign, FiKey, FiActivity, FiTrendingUp,
  FiAlertCircle, FiCheckCircle, FiClock, FiServer
} from 'react-icons/fi';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';

// Types
interface DashboardStats {
  totalClients: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  pendingLicenses: number;
  systemHealth: number;
  newClientsThisMonth: number;
}

interface RecentActivity {
  id: string;
  type: 'subscription' | 'license' | 'payment' | 'support';
  client: string;
  description: string;
  timestamp: string;
  status: 'success' | 'pending' | 'warning';
}

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xl};
`;

const Header = styled.div`
  background: ${theme.colors.primary};
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  color: white;
  box-shadow: ${theme.shadows.lg};
`;

const HeaderTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  margin: 0 0 ${theme.spacing.xs} 0;
`;

const HeaderSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  margin: 0;
  opacity: 0.95;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing.lg};
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  padding: ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  border-left: 4px solid ${props => props.color || theme.colors.primary};
  transition: all ${theme.transitions.fast};
  cursor: pointer;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.md};
  }
`;

const StatHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: ${theme.spacing.md};
`;

const StatIcon = styled.div<{ $bgColor?: string }>`
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.$bgColor || theme.colors.primary}20;
  color: ${props => props.$bgColor || theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

const StatValue = styled.div`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const StatLabel = styled.div`
  font-size: ${theme.typography.sizes.sm};
  color: ${theme.colors.text}80;
`;

const StatTrend = styled.div<{ $positive?: boolean }>`
  font-size: ${theme.typography.sizes.xs};
  color: ${props => props.$positive ? theme.colors.success : theme.colors.error};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const ViewAllLink = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    color: ${theme.colors.primary}DD;
    text-decoration: underline;
  }
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const ActivityItem = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface}50;
  border-radius: ${theme.borderRadius.md};
  display: flex;
  gap: ${theme.spacing.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
  }
`;

const ActivityIcon = styled.div<{ status: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;

  ${props => {
    switch (props.status) {
      case 'success':
        return `
          background: ${theme.colors.success}20;
          color: ${theme.colors.success};
        `;
      case 'pending':
        return `
          background: ${theme.colors.warning}20;
          color: ${theme.colors.warning};
        `;
      case 'warning':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text};
        `;
    }
  }}
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
`;

const ActivityDescription = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}60;
`;

const ActivityTime = styled.div`
  font-size: ${theme.typography.sizes.xs};
  color: ${theme.colors.text}40;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${theme.spacing.md};
`;

const QuickActionButton = styled.button`
  padding: ${theme.spacing.lg};
  background: ${theme.colors.surface}50;
  border: 2px solid ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: white;
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
    transform: translateX(4px);
  }

  svg {
    font-size: 20px;
  }
`;

export const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const stats: DashboardStats = {
    totalClients: 127,
    activeSubscriptions: 115,
    monthlyRevenue: 45670,
    pendingLicenses: 8,
    systemHealth: 99.8,
    newClientsThisMonth: 12
  };

  const recentActivity: RecentActivity[] = [
    {
      id: '1',
      type: 'subscription',
      client: 'Office HLM Paris',
      description: 'Nouveau abonnement Premium - 50 utilisateurs',
      timestamp: '2024-01-20T14:30:00',
      status: 'success'
    },
    {
      id: '2',
      type: 'license',
      client: 'Habitat Social Lyon',
      description: 'Demande de renouvellement de licence',
      timestamp: '2024-01-20T13:15:00',
      status: 'pending'
    },
    {
      id: '3',
      type: 'payment',
      client: 'Logement Social Marseille',
      description: 'Paiement mensuel reçu - 890 FCFA',
      timestamp: '2024-01-20T11:00:00',
      status: 'success'
    },
    {
      id: '4',
      type: 'support',
      client: 'Immobilier Social Toulouse',
      description: 'Ticket de support prioritaire ouvert',
      timestamp: '2024-01-20T09:45:00',
      status: 'warning'
    },
    {
      id: '5',
      type: 'subscription',
      client: 'HLM Bordeaux',
      description: 'Mise à niveau vers plan Enterprise',
      timestamp: '2024-01-19T16:20:00',
      status: 'success'
    }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'subscription': return <FiUsers />;
      case 'license': return <FiKey />;
      case 'payment': return <FiDollarSign />;
      case 'support': return <FiAlertCircle />;
      default: return <FiActivity />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return `Il y a ${diffDays}j`;
  };

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <HeaderTitle>🚀 Super Admin - Pradium Tech</HeaderTitle>
          <HeaderSubtitle>
            Plateforme de gestion P-Screen - Vue d'ensemble et contrôle système
          </HeaderSubtitle>
        </Header>

        <StatsGrid>
          <StatCard color={theme.colors.primary} onClick={() => navigate('/superadmin/clients')}>
            <StatHeader>
              <div>
                <StatValue>{stats.totalClients}</StatValue>
                <StatLabel>Clients Total</StatLabel>
                <StatTrend $positive>
                  <FiTrendingUp /> +{stats.newClientsThisMonth} ce mois
                </StatTrend>
              </div>
              <StatIcon $bgColor={theme.colors.primary}>
                <FiUsers />
              </StatIcon>
            </StatHeader>
          </StatCard>

          <StatCard color={theme.colors.success} onClick={() => navigate('/superadmin/subscriptions')}>
            <StatHeader>
              <div>
                <StatValue>{stats.activeSubscriptions}</StatValue>
                <StatLabel>Abonnements Actifs</StatLabel>
                <StatTrend $positive>
                  <FiCheckCircle /> {Math.round((stats.activeSubscriptions / stats.totalClients) * 100)}% taux d'activation
                </StatTrend>
              </div>
              <StatIcon $bgColor={theme.colors.success}>
                <FiCheckCircle />
              </StatIcon>
            </StatHeader>
          </StatCard>

          <StatCard color={theme.colors.info} onClick={() => navigate('/superadmin/revenue')}>
            <StatHeader>
              <div>
                <StatValue>{stats.monthlyRevenue.toLocaleString()} FCFA</StatValue>
                <StatLabel>Revenu Mensuel (MRR)</StatLabel>
                <StatTrend $positive>
                  <FiTrendingUp /> +15% vs mois dernier
                </StatTrend>
              </div>
              <StatIcon $bgColor={theme.colors.info}>
                <FiDollarSign />
              </StatIcon>
            </StatHeader>
          </StatCard>

          <StatCard color={theme.colors.warning} onClick={() => navigate('/superadmin/licenses')}>
            <StatHeader>
              <div>
                <StatValue>{stats.pendingLicenses}</StatValue>
                <StatLabel>Licences en Attente</StatLabel>
                <StatTrend>
                  <FiClock /> Action requise
                </StatTrend>
              </div>
              <StatIcon $bgColor={theme.colors.warning}>
                <FiKey />
              </StatIcon>
            </StatHeader>
          </StatCard>

          <StatCard color={theme.colors.success}>
            <StatHeader>
              <div>
                <StatValue>{stats.systemHealth}%</StatValue>
                <StatLabel>Santé Système</StatLabel>
                <StatTrend $positive>
                  <FiCheckCircle /> Tous les services opérationnels
                </StatTrend>
              </div>
              <StatIcon $bgColor={theme.colors.success}>
                <FiServer />
              </StatIcon>
            </StatHeader>
          </StatCard>
        </StatsGrid>

        <ContentGrid>
          <Section>
            <SectionHeader>
              <SectionTitle>
                <FiActivity /> Activité Récente
              </SectionTitle>
              <ViewAllLink onClick={() => navigate('/superadmin/activity')}>
                Voir tout
              </ViewAllLink>
            </SectionHeader>

            <ActivityList>
              {recentActivity.map(activity => (
                <ActivityItem key={activity.id}>
                  <ActivityIcon status={activity.status}>
                    {getActivityIcon(activity.type)}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>{activity.client}</ActivityTitle>
                    <ActivityDescription>{activity.description}</ActivityDescription>
                    <ActivityTime>
                      <FiClock />
                      {formatTimestamp(activity.timestamp)}
                    </ActivityTime>
                  </ActivityContent>
                </ActivityItem>
              ))}
            </ActivityList>
          </Section>

          <Section>
            <SectionHeader>
              <SectionTitle>
                ⚡ Actions Rapides
              </SectionTitle>
            </SectionHeader>

            <QuickActions>
              <QuickActionButton onClick={() => navigate('/superadmin/clients/new')}>
                <FiUsers />
                Ajouter un Client
              </QuickActionButton>

              <QuickActionButton onClick={() => navigate('/superadmin/licenses/generate')}>
                <FiKey />
                Générer une Licence
              </QuickActionButton>

              <QuickActionButton onClick={() => navigate('/superadmin/subscriptions')}>
                <FiCheckCircle />
                Gérer Abonnements
              </QuickActionButton>

              <QuickActionButton onClick={() => navigate('/superadmin/revenue')}>
                <FiDollarSign />
                Rapports Financiers
              </QuickActionButton>

              <QuickActionButton onClick={() => navigate('/superadmin/system')}>
                <FiServer />
                État du Système
              </QuickActionButton>
            </QuickActions>
          </Section>
        </ContentGrid>
      </Container>
    </DashboardLayout>
  );
};
